import { useCallback, useMemo, useState } from 'react';
import { useInterval } from 'react-use';
import { OperationContext } from 'urql';

import { OkrTaskCreationConfig, SyncStatusEnum } from '@/types/schema';

const MAXIMUM_RETRY = 4;
const RETRY_DELAY = 10000;

const useSubscribeToSyncConfig = ({
  taskCreationConfigs,
  executeQuery,
}: {
  taskCreationConfigs?: OkrTaskCreationConfig[];
  executeQuery?: (opts?: Partial<OperationContext>) => void;
}) => {
  const [retryCount, setRetryCount] = useState<number>(0);

  const isSubscribed = useMemo(
    () =>
      (taskCreationConfigs?.findIndex(
        (taskCreationConfig) =>
          taskCreationConfig.syncConfig?.syncStatus !== SyncStatusEnum.Completed,
      ) ?? -1) > -1,
    [taskCreationConfigs],
  );

  const isRetry = useMemo(
    () => isSubscribed && retryCount < MAXIMUM_RETRY,
    [isSubscribed, retryCount],
  );

  useInterval(
    () => {
      executeQuery?.({ requestPolicy: 'network-only' });
      setRetryCount((prevState) => prevState + 1);
    },
    isRetry ? RETRY_DELAY : null,
  );

  const resetRetryCount = useCallback(() => {
    setRetryCount(0);
  }, []);

  return { resetRetryCount, isRetry };
};

export default useSubscribeToSyncConfig;
